import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TerminalTheme, TimestampMode } from '@portal-core/live-log/components/live-log/live-log.component';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';

/** Subset of fields that can be updated by the dialog — change handlers pass a Partial of this. */
export interface LiveLogSettings {
  showIcons: boolean;
  timestampMode: TimestampMode;
  theme: TerminalTheme;
}

/**
 * Input shape for the LiveLogSettingsDialog. Seeded with the caller's current values; the
 * `apply` callback is invoked on each individual control change so settings take effect
 * immediately (no Save/Cancel — the user just closes the dialog when they're done).
 */
export interface LiveLogSettingsDialogData extends LiveLogSettings {
  apply: (changes: Partial<LiveLogSettings>) => void;
}

/**
 * LiveLogSettingsDialogComponent
 * Dialog for adjusting a live log's display preferences (theme, wrap, timestamps, icons).
 * Each control change is applied immediately via the caller-provided `apply` callback,
 * mirroring how a cog dropdown menu would behave but with more room for radio groups instead
 * of submenus.
 */
@Component({
  selector: 'mc-live-log-settings-dialog',
  templateUrl: './live-log-settings-dialog.component.html',
  styleUrls: ['./live-log-settings-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiveLogSettingsDialogComponent extends DialogBase<LiveLogSettingsDialogComponent, void> implements OnInit {
  /** Default config when opening the dialog. */
  static DialogConfig: MatDialogConfig = {
    width: '40rem',
    autoFocus: false
  };

  showIcons: boolean;
  timestampMode: TimestampMode;
  theme: TerminalTheme;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: LiveLogSettingsDialogData,
    protected dialog: MatDialog,
    protected dialogRef: MatDialogRef<LiveLogSettingsDialogComponent, void>
  ) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    super.ngOnInit();
    this.showIcons = this.data.showIcons;
    this.timestampMode = this.data.timestampMode;
    this.theme = this.data.theme;
  }

  /** Forward a single-field change to the caller for immediate application. */
  applyChange(changes: Partial<LiveLogSettings>) {
    this.data.apply(changes);
  }
}
